// The KPI tile: how many requesters were answered inside the clock this week
// and this month, the median first response, and what is overdue right now.
// Every number is from inbox_kpi_v1, which reads the ticket tables directly.
//
// A missing figure shows as missing. "No replies yet" is not "0m", and a week
// with no email reads "None received yet" rather than 0%.
import { cn } from '../../lib/shadcn/utils'
import { medianLabel, shareLabel } from '../../utils/inbox'
import type { Kpi } from './types'

function Tile({ title, value, sub, alarm }: { title: string; value: string; sub?: string; alarm?: boolean }) {
  return (
    <div
      className={cn(
        'min-w-0 rounded-lg border bg-card px-3 py-2',
        alarm ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))]' : 'border-border',
      )}
    >
      <p className="text-[11px] font-medium uppercase leading-tight tracking-wide text-muted-foreground">{title}</p>
      <p
        className={cn(
          'truncate text-base font-semibold tabular-nums',
          alarm ? 'text-[hsl(var(--brand-soft-foreground))]' : 'text-foreground',
        )}
      >
        {value}
      </p>
      {sub && <p className="text-[11px] leading-snug text-muted-foreground">{sub}</p>}
    </div>
  )
}

export function KpiTiles({ kpi, error }: { kpi: Kpi | null; error: string | null }) {
  if (error) {
    return <p className="px-4 py-2 text-xs text-rose-700 dark:text-rose-300">The figures could not be read: {error}</p>
  }
  if (!kpi) return <div className="h-[68px]" />
  const w = kpi.periods.week
  const m = kpi.periods.month
  const hrs = `${kpi.hours} hours`
  const pending = (p: typeof w) =>
    [
      p.answered_late ? `${p.answered_late} late` : null,
      p.waiting_overdue ? `${p.waiting_overdue} still waiting, overdue` : null,
      p.waiting_in_time ? `${p.waiting_in_time} still inside the clock` : null,
      p.closed_without_reply ? `${p.closed_without_reply} closed without an email reply` : null,
    ]
      .filter(Boolean)
      .join(', ')
  return (
    <div className="grid grid-cols-2 gap-2 px-4 py-3 lg:grid-cols-4">
      <Tile
        title={`Answered within ${hrs}, this week`}
        value={shareLabel(w.answered_in_time, w.received)}
        sub={pending(w) || 'Of the email received since Monday'}
      />
      <Tile
        title={`Answered within ${hrs}, this month`}
        value={shareLabel(m.answered_in_time, m.received)}
        sub={pending(m) || 'Of the email received since the 1st'}
      />
      <Tile
        title="Median first response"
        value={medianLabel(w.median_first_response_seconds)}
        sub={`This week. This month: ${medianLabel(m.median_first_response_seconds)}`}
      />
      <Tile
        title="Open and overdue"
        value={String(kpi.open_overdue)}
        sub={`${kpi.open_awaiting} waiting for a reply, ${kpi.open_total} open in all`}
        alarm={kpi.open_overdue > 0}
      />
    </div>
  )
}
